import React from "react";
import { Box, Pagination, Typography } from "@mui/material";
import ComboBox from "./ComboBox";

/**
 * 페이지 크기 선택지 — 기본 100개.
 *
 * <p>업무 표는 "훑어보고 Ctrl+F 로 찾는" 쓰임이 대부분이라 10~20건씩 끊으면 페이지를 계속 넘겨야 한다.
 * 표가 자연 높이로 늘어나고 스크롤은 부모가 담당하므로, 한 페이지가 커도 화면이 망가지지 않는다.
 */
const DEFAULT_SIZE_OPTIONS = [50, 100, 200, 500];
export const DEFAULT_PAGE_SIZE = 100;

/**
 * "N개씩 보기" 선택 — <b>표 우측 상단</b>에 두는 공용 컨트롤.
 *
 * <h3>왜 페이지 이동과 떼어놨나</h3>
 * <p>둘은 쓰는 시점이 다르다. 페이지 크기는 표를 <b>보기 전에</b> 한 번 정하는 설정이고, 페이지 이동은
 * 목록을 <b>다 읽은 뒤</b> 하는 행동이다. 그래서 크기 선택은 표 머리맡(우측 상단)에 붙여두고,
 * 페이지 이동은 표 끝에 둔다. 아래에 같이 묶어두면 크기를 바꾸려고 매번 표 끝까지 내려가야 한다.
 *
 * <p>폭을 넉넉히(150px) 잡은 이유 — "500개씩" 같은 라벨이 잘리지 않아야 하고, 화살표까지 포함해
 * 누르기 편한 과녁이 되어야 한다.
 *
 * @param {number}   value            현재 페이지 크기
 * @param {function} onChange         변경 콜백 (숫자로 전달)
 * @param {number[]} [options]        선택지 (기본 50/100/200/500)
 */
export function TableSizeSelect({ value = DEFAULT_PAGE_SIZE, onChange, options = DEFAULT_SIZE_OPTIONS }) {
    return (
        <Box sx={{ width: 150, flexShrink: 0, ml: "auto" }}>
            <ComboBox clearable={false}
                options={options.map(n => ({ value: n, label: `${n}개씩 보기` }))}
                value={value}
                onChange={v => onChange?.(Number(v))} />
        </Box>
    );
}

/**
 * 공통 페이지 네비게이터 — <b>시스템 내 모든 표가 이걸 쓴다.</b>
 *
 * <h3>왜 공용으로 모았나</h3>
 * <p>예전엔 화면마다 제각각이었다. 어떤 곳은 MUI {@code <Pagination>} 을 직접 깔고, 어떤 곳은
 * {@code 이전/다음} 버튼을 손으로 만들고, 페이지 크기 선택지도 화면마다 달랐다([10,20,30], [5,10,20]…).
 * 그래서 "표마다 조작법이 다른" 상태였고 기본값 하나 바꾸려면 십여 곳을 찾아다녀야 했다.
 * 이제 규칙은 여기 한 곳에만 있다 — 위치·크기 선택지·기본 페이지 크기 전부.
 *
 * <h3>위치 — 표 아래, 가운데</h3>
 * <p>표의 끝이 곧 "다음 장으로" 를 누르는 자리다. 목록을 다 읽고 자연스럽게 시선이 닿는 곳이라
 * 아래쪽에 둔다. 가로로는 가운데 정렬해 표 폭이 달라져도 위치가 흔들리지 않게 한다.
 *
 * <p>페이지 <b>크기</b> 선택은 여기가 아니라 표 우측 상단({@link TableSizeSelect})에 있다 — 이유는
 * 그 컴포넌트 주석 참고.
 *
 * @param {number}   totalPages        백엔드 페이지네이션 시 직접 전달
 * @param {number}   [total]           클라이언트 페이지네이션 시 전체 항목 수
 * @param {number}   [pageSize]        페이지 크기 (total 로 페이지 수·범위를 계산할 때 필요)
 * @param {number}   page              현재 페이지 (1-based)
 * @param {function} onPageChange      페이지 변경 콜백
 */
export default function TablePaginator({
    totalPages,
    total,
    pageSize = DEFAULT_PAGE_SIZE,
    page = 1,
    onPageChange,
}) {
    const pages = totalPages ?? (total != null ? Math.ceil(total / pageSize) || 1 : 1);

    // 범위 계산 (total이 있을 때만)
    const from = total != null ? (page - 1) * pageSize + 1 : null;
    const to   = total != null ? Math.min(page * pageSize, total) : null;

    return (
        <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center",
            flexWrap: "wrap", gap: 1.5,
            py: 1.5, px: 2, flexShrink: 0,
            borderTop: "1px solid #f1f5f9",
        }}>
            <Pagination
                count={Math.max(pages, 1)} page={page}
                onChange={(_, v) => onPageChange?.(v)}
                size="small" color="primary"
            />

            {from != null && (
                <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                    {from}–{to} / 전체 <b style={{ color: "#475569" }}>{total}</b>건
                </Typography>
            )}
        </Box>
    );
}
